if (!customElements.get('jl-horizontal-carousel')) {
  customElements.define('jl-horizontal-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('[data-horizontal-track]');
      this.cards = this.track ? [...this.track.children] : [];
      if (!this.cards.length) return;
      this.navigation = this.querySelector('[data-horizontal-nav]');
      this.previous = this.querySelector('[data-horizontal-prev]');
      this.next = this.querySelector('[data-horizontal-next]');
      this.currentLabel = this.querySelector('[data-horizontal-current]');
      this.totalLabel = this.querySelector('[data-horizontal-total]');
      this.pagination = this.querySelector('[data-horizontal-pagination]');
      this.wantsAutoplay = this.dataset.autoplay === 'true' && this.cards.length > 1;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.interval = Math.max(1000, Number(this.dataset.interval) || 3000);
      this.resumeDelay = Math.max(1000, Number(this.dataset.resumeDelay) || 3000);
      this.loop = false;
      this.positions = [0];
      this.page = 0;
      this.hovered = false;
      this.pressed = false;
      this.inView = false;
      this.editorSelected = false;
      this.track.querySelectorAll('img').forEach(image => { image.draggable = false; });
      this.previous?.addEventListener('click', () => { this.go(this.page - 1); this.schedule(this.resumeDelay); }, { signal });
      this.next?.addEventListener('click', () => { this.go(this.page + 1); this.schedule(this.resumeDelay); }, { signal });
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.normalize(), 140);
      }, { passive: true, signal });
      this.track.addEventListener('scrollend', () => this.normalize(), { signal });
      this.track.addEventListener('keydown', event => {
        if (event.defaultPrevented) return;
        const keys = { ArrowLeft: this.page - 1, ArrowRight: this.page + 1, Home: 0, End: this.loop ? this.cards.length - 1 : this.positions.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        this.go(keys[event.key]);
        this.schedule(this.resumeDelay);
      }, { signal });
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; clearTimeout(this.autoTimer); } }, { signal });
      this.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') { this.hovered = false; this.schedule(this.resumeDelay); } }, { signal });
      this.addEventListener('pointerdown', () => { this.pressed = true; clearTimeout(this.autoTimer); }, { signal });
      const resume = () => { this.pressed = false; this.schedule(this.resumeDelay); };
      window.addEventListener('pointerup', resume, { signal });
      window.addEventListener('pointercancel', resume, { signal });
      this.addEventListener('focusin', () => clearTimeout(this.autoTimer), { signal });
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule(this.resumeDelay)), { signal });
      this.track.addEventListener('wheel', () => this.schedule(this.resumeDelay), { passive: true, signal });
      document.addEventListener('visibilitychange', () => this.schedule(this.resumeDelay), { signal });
      this.reducedMotion.addEventListener('change', () => this.schedule(this.resumeDelay), { signal });
      this.track.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.track.addEventListener('pointerdown', event => {
        this.suppressClick = false;
        if (this.dataset.draggable === 'false' || event.pointerType !== 'mouse' || event.button !== 0 || this.positions.length < 2 || event.target.closest('button,input,select,textarea')) return;
        this.drag = { id: event.pointerId, x: event.clientX, start: this.track.scrollLeft, moved: false };
        this.suppressClick = false;
      }, { signal });
      this.track.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(distance) < 6) return;
        if (!this.drag.moved) { this.drag.moved = true; this.track.classList.add('is-dragging'); this.track.setPointerCapture(event.pointerId); }
        this.track.scrollLeft = this.drag.start - distance;
      }, { signal });
      const finishDrag = event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const drag = this.drag;
        this.drag = null;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-dragging');
        if (drag.moved) { this.suppressClick = true; this.normalize(); this.update(); this.go(this.page); }
        this.schedule(this.resumeDelay);
      };
      this.track.addEventListener('pointerup', finishDrag, { signal });
      this.track.addEventListener('pointercancel', finishDrag, { signal });
      this.track.addEventListener('lostpointercapture', finishDrag, { signal });
      this.track.addEventListener('click', event => { if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; } }, { capture: true, signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(card => card === event.target || card.contains(event.target));
        if (index < 0) return;
        this.editorSelected = true;
        clearTimeout(this.autoTimer);
        this.go(index);
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(this.resumeDelay); }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; this.schedule(this.interval); }, { threshold: 0.15 });
      this.intersectionObserver.observe(this);
      this.measure();
    }
    measure() {
      if (!this.controller || !this.isConnected) return;
      const first = this.cards[0], last = this.cards[this.cards.length - 1];
      const naturalWidth = last.offsetLeft - first.offsetLeft + last.offsetWidth;
      const shouldLoop = this.wantsAutoplay && (this.dataset.loop === 'true' || naturalWidth > this.track.clientWidth + 1);
      if (shouldLoop !== this.loop) {
        this.track.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
        this.loop = shouldLoop;
        if (this.loop) {
          for (let repeat = 0; repeat < 2; repeat++) for (const card of this.cards) {
            const clone = card.cloneNode(true);
            clone.dataset.carouselClone = '';
            clone.setAttribute('aria-hidden', 'true');
            clone.removeAttribute('data-shopify-editor-block');
            clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
            clone.querySelectorAll('[data-shopify-editor-block]').forEach(element => element.removeAttribute('data-shopify-editor-block'));
            clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(element => { element.tabIndex = -1; });
            this.track.append(clone);
          }
        }
        this.track.scrollLeft = 0;
        this.pagination?.setAttribute('aria-live', this.loop ? 'off' : 'polite');
      }
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.positions = [0];
      if (maximum > 1) {
        const candidates = this.loop ? [...this.cards, this.track.querySelector('[data-carousel-clone]')] : this.cards;
        for (const card of candidates) {
          const position = Math.min(maximum, Math.max(0, card.offsetLeft - first.offsetLeft));
          if (position - this.positions[this.positions.length - 1] > 1) this.positions.push(position);
        }
      }
      this.cycle = this.loop ? this.positions[this.positions.length - 1] : 0;
      if (this.navigation) this.navigation.hidden = this.positions.length <= 1;
      this.track.classList.toggle('is-draggable', this.positions.length > 1 && this.dataset.draggable !== 'false');
      if (this.totalLabel) this.totalLabel.textContent = this.loop ? this.cards.length : this.positions.length;
      this.update();
      this.schedule(this.interval);
    }
    update() {
      let left = this.track.scrollLeft;
      if (this.loop && this.cycle) left %= this.cycle;
      const positions = this.loop ? this.positions.slice(0, -1) : this.positions;
      this.page = positions.reduce((best, value, i) => Math.abs(value - left) < Math.abs(positions[best] - left) ? i : best, 0);
      if (this.currentLabel) this.currentLabel.textContent = this.page + 1;
      if (this.previous) this.previous.disabled = !this.loop && this.page === 0;
      if (this.next) this.next.disabled = !this.loop && this.page === this.positions.length - 1;
    }
    normalize() {
      if (!this.loop || !this.cycle || this.drag) return;
      if (this.track.scrollLeft >= this.cycle - 1) { this.track.scrollTo({ left: this.track.scrollLeft % this.cycle, behavior: 'instant' }); this.update(); }
    }
    go(index) {
      if (this.loop) {
        this.normalize();
        if (index < 0) { this.track.scrollTo({ left: this.cycle, behavior: 'instant' }); index = this.cards.length - 1; }
        index = Math.min(this.cards.length, Math.max(0, index));
      } else index = Math.min(this.positions.length - 1, Math.max(0, index));
      this.track.scrollTo({ left: this.positions[index], behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }
    hasKeyboardFocus() {
      const active = document.activeElement;
      return !!active && this.contains(active) && active.matches(':focus-visible');
    }

    schedule(delay = this.interval) {
      clearTimeout(this.autoTimer);
      if (!this.isConnected || !this.controller || !this.loop || !this.inView || document.hidden || this.hovered || this.pressed || this.editorSelected || this.hasKeyboardFocus() || this.reducedMotion.matches) return;
      this.autoTimer = setTimeout(() => { this.normalize(); this.update(); this.go(this.page + 1); this.schedule(this.interval); }, delay);
    }
    disconnectedCallback() {
      this.controller?.abort();this.resizeObserver?.disconnect();this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.frame);clearTimeout(this.autoTimer);clearTimeout(this.settleTimer);
      this.track?.querySelectorAll('[data-carousel-clone]').forEach(clone => clone.remove());
      this.controller = null;this.drag = null;
    }
  });
}
